/**
 * LDRParticles - Liquid Droplet Radiator visualization
 * Particle system representing tin droplets ejecting heat
 * Trails behind the EAHP swarm rotation
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';
import { LDR_DATA } from '../../data/scientificData';

// === PRE-ALLOCATED VECTORS FOR PERFORMANCE ===
// Avoids creating thousands of objects every frame
const _sunPos = new THREE.Vector3();
const _currentPos = new THREE.Vector3();
const _tangent = new THREE.Vector3();
const _radial = new THREE.Vector3();

interface LDRParticlesProps {
  sunPosition?: [number, number, number];
  orbitRadius?: number;
  particleCount?: number;
}

export function LDRParticles({
  sunPosition = [-30, 0, 0],
  orbitRadius = 8,
  particleCount = 2000
}: LDRParticlesProps) {
  const pointsRef = useRef<THREE.Points>(null);
  const { view, isPaused, animationSpeed } = useStore();

  // Initialize particle data
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const temperatures = new Float32Array(particleCount);
    const lifetimes = new Float32Array(particleCount);
    const sizes = new Float32Array(particleCount);

    const sunPos = new THREE.Vector3(...sunPosition);

    for (let i = 0; i < particleCount; i++) {
      // Start on the swarm ring (back side, away from sun)
      const theta = Math.random() * Math.PI * 2;
      const phi = (Math.random() - 0.5) * 0.3;

      // Position slightly behind the satellite (opposite sun direction)
      const radialOffset = 0.5 + Math.random() * 1;
      const x = sunPos.x + Math.cos(theta) * (orbitRadius + radialOffset);
      const y = sunPos.y + Math.sin(phi) * orbitRadius * 0.3;
      const z = sunPos.z + Math.sin(theta) * (orbitRadius + radialOffset);

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      // Velocity: tangential to orbit + radial outward
      const tangent = new THREE.Vector3(-Math.sin(theta), 0, Math.cos(theta)).normalize();
      const radial = new THREE.Vector3(
        x - sunPos.x,
        y - sunPos.y,
        z - sunPos.z
      ).normalize();

      const speed = 0.02 + Math.random() * 0.03;
      velocities[i * 3] = (tangent.x * 0.5 + radial.x * 0.5) * speed;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.01;
      velocities[i * 3 + 2] = (tangent.z * 0.5 + radial.z * 0.5) * speed;

      // Temperature: starts hot (800K), cools as it travels
      temperatures[i] = 0.8 + Math.random() * 0.2;

      // Lifetime tracking
      lifetimes[i] = Math.random(); // 0-1 progress through lifecycle

      // Size (100 μm droplets, but scaled for visibility)
      sizes[i] = 0.02 + Math.random() * 0.03;
    }

    return { positions, velocities, temperatures, lifetimes, sizes };
  }, [sunPosition, orbitRadius, particleCount]);

  // Generate colors based on temperature (vertex colors approach)
  const colors = useMemo(() => {
    const col = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const temp = particles.temperatures[i];
      // Hot = orange/yellow (1.0, 0.8, 0.3), Cool = red (0.8, 0.2, 0.1)
      col[i * 3] = 0.8 + temp * 0.2;
      col[i * 3 + 1] = 0.2 + temp * 0.6;
      col[i * 3 + 2] = 0.1 + temp * 0.2;
    }
    return col;
  }, [particles.temperatures, particleCount]);

  const colorsRef = useRef<Float32Array>(colors);

  useFrame(() => {
    if (!pointsRef.current || isPaused || !view.showLDRParticles) return;

    const pos = pointsRef.current.geometry.attributes.position.array as Float32Array;
    const col = pointsRef.current.geometry.attributes.color?.array as Float32Array;
    const lifes = particles.lifetimes;

    // PERFORMANCE: Use pre-allocated vector
    _sunPos.set(sunPosition[0], sunPosition[1], sunPosition[2]);

    for (let i = 0; i < particleCount; i++) {
      // Update position
      pos[i * 3] += particles.velocities[i * 3] * animationSpeed;
      pos[i * 3 + 1] += particles.velocities[i * 3 + 1] * animationSpeed;
      pos[i * 3 + 2] += particles.velocities[i * 3 + 2] * animationSpeed;

      // Cool down as droplet travels
      particles.temperatures[i] *= 0.998;

      // Update color based on temperature
      if (col) {
        const temp = particles.temperatures[i];
        col[i * 3] = 0.8 + temp * 0.2;
        col[i * 3 + 1] = 0.2 + temp * 0.6;
        col[i * 3 + 2] = 0.1 + temp * 0.2;
      }

      // Update lifetime
      lifes[i] += 0.002 * animationSpeed;

      // Check if droplet should be "collected" and reset
      // PERFORMANCE: Use pre-allocated vector instead of new Vector3
      _currentPos.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);

      const distFromOrbit = _currentPos.distanceTo(_sunPos);

      // Reset if too far from orbit or lifetime expired
      if (distFromOrbit > orbitRadius + 3 || lifes[i] > 1 || particles.temperatures[i] < 0.2) {
        // Reset to new position on swarm ring
        const theta = Math.random() * Math.PI * 2;
        const phi = (Math.random() - 0.5) * 0.3;
        const radialOffset = 0.5 + Math.random() * 1;

        pos[i * 3] = _sunPos.x + Math.cos(theta) * (orbitRadius + radialOffset);
        pos[i * 3 + 1] = _sunPos.y + Math.sin(phi) * orbitRadius * 0.3;
        pos[i * 3 + 2] = _sunPos.z + Math.sin(theta) * (orbitRadius + radialOffset);

        // Reset temperature (hot from absorbing laser heat)
        particles.temperatures[i] = 0.8 + Math.random() * 0.2;
        lifes[i] = 0;

        // New velocity - PERFORMANCE: Use pre-allocated vectors
        _tangent.set(-Math.sin(theta), 0, Math.cos(theta)).normalize();
        _radial.set(
          pos[i * 3] - _sunPos.x,
          pos[i * 3 + 1] - _sunPos.y,
          pos[i * 3 + 2] - _sunPos.z
        ).normalize();

        const speed = 0.02 + Math.random() * 0.03;
        particles.velocities[i * 3] = (_tangent.x * 0.5 + _radial.x * 0.5) * speed;
        particles.velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.01;
        particles.velocities[i * 3 + 2] = (_tangent.z * 0.5 + _radial.z * 0.5) * speed;
      }
    }

    pointsRef.current.geometry.attributes.position.needsUpdate = true;
    if (pointsRef.current.geometry.attributes.color) {
      pointsRef.current.geometry.attributes.color.needsUpdate = true;
    }
  });

  if (!view.showLDRParticles) return null;

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={particleCount}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.15}
        vertexColors
        transparent
        opacity={0.8}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        sizeAttenuation
      />
    </points>
  );
}

// Export LDR data for info panel
export function useLDRData() {
  return LDR_DATA;
}
